import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { ARENA_SIZE, BASE_SIZE } from '../../../utils/constants.js';
import { matte, colored, vary, seededRandom, fbm, noise2, jitter, Fader } from '../../Style.js';
import { SEA, seaTime, foamFrame, foamMesh, palmParts, rockGeometry, buoyParts, ropeParts } from './style.js';

const HALF = ARENA_SIZE / 2;
const WALL_HEIGHT = 5;
const WALL_THICKNESS = 1.5;
const OCEAN_SIZE = 3600;
const OCEAN_LEVEL = -0.25;

// Sandbar shallows marking the routes: base to base, and out to the forward islands.
const PATHS = [
  [[-80, 0], [-60, 3], [-30, -3], [0, 0], [30, 3], [60, -3], [80, 0]],
  [[0, 0], [-3, -18], [2, -34], [0, -50]],
  [[0, 0], [3, 18], [-2, 34], [0, 50]]
];

function smoothstep(a, b, x) {
  const t = Math.max(0, Math.min(1, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
}

function distanceToSegment(px, pz, ax, az, bx, bz) {
  const dx = bx - ax;
  const dz = bz - az;
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (pz - az) * dz) / (dx * dx + dz * dz)));
  return Math.hypot(px - (ax + dx * t), pz - (az + dz * t));
}

function pathDistance(x, z) {
  let best = Infinity;
  for (const path of PATHS) {
    for (let i = 0; i < path.length - 1; i++) {
      best = Math.min(best, distanceToSegment(x, z, path[i][0], path[i][1], path[i + 1][0], path[i + 1][1]));
    }
  }
  // Sandbars meander rather than run ruler-straight
  return best + noise2(x * 0.08, z * 0.08) * 2.2;
}

// Distance from the edge of either base island's footprint
function baseDistance(x, z) {
  const hx = BASE_SIZE / 2;
  const hz = (BASE_SIZE * 1.15) / 2;
  let best = Infinity;
  for (const bx of [-90, 90]) {
    const dx = Math.max(Math.abs(x - bx) - hx, 0);
    const dz = Math.max(Math.abs(z) - hz, 0);
    best = Math.min(best, Math.hypot(dx, dz));
  }
  return best;
}

// How far inside a base harbour a point is: 1 in the sheltered water, 0 out in the lagoon
function harbourCalm(x, z) {
  const hx = BASE_SIZE / 2;
  const hz = (BASE_SIZE * 1.15) / 2;
  let best = 0;
  for (const bx of [-90, 90]) {
    const dx = Math.abs(x - bx) - hx;
    const dz = Math.abs(z) - hz;
    best = Math.max(best, 1 - smoothstep(-3, 1, Math.max(dx, dz)));
  }
  return best;
}

// How shallow the lagoon is at a point: 0 is deep channel, 1 is a sandbar.
function shallowness(x, z) {
  const lane = 1 - smoothstep(2, 6.5, pathDistance(x, z));
  const base = 1 - smoothstep(0, 14, baseDistance(x, z));
  const patch = smoothstep(0.25, 0.5, fbm(x / 26 + 4, z / 26 - 2, 3));
  return Math.min(1, Math.max(lane * 0.9, base, patch * 0.55));
}

// The lagoon and ocean share one animated water material: gentle swell,
// fine ripples that catch the sun, and caustics in the shallows.
function waterMaterial(roughness, causticScale) {
  const material = new THREE.MeshStandardMaterial({
    vertexColors: true,
    roughness,
    metalness: 0.05
  });
  material.onBeforeCompile = shader => {
    shader.uniforms.uTime = seaTime;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', /* glsl */`#include <common>
        uniform float uTime;
        attribute float aShallow;
        attribute float aCalm;
        varying float vShallow;
        varying float vCalm;
        varying vec3 vWaterPos;
      `)
      .replace('#include <begin_vertex>', /* glsl */`#include <begin_vertex>
        vec3 wp = (modelMatrix * vec4(transformed, 1.0)).xyz;
        transformed.y += (sin(wp.x * 0.25 + uTime * 1.2) * 0.05 + sin(wp.z * 0.31 - uTime * 0.9) * 0.04) * (1.0 - aCalm);
        vWaterPos = wp;
        vShallow = aShallow;
        vCalm = aCalm;
      `);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', /* glsl */`#include <common>
        uniform float uTime;
        varying float vShallow;
        varying float vCalm;
        varying vec3 vWaterPos;
        float caustic(vec2 p, float t) {
          float a = sin(p.x * 1.1 + sin(p.y * 0.9 + t) * 1.4 + t * 0.6);
          float b = sin(p.y * 1.3 + sin(p.x * 0.8 - t * 0.8) * 1.4 - t * 0.5);
          float c = sin((p.x + p.y) * 0.7 + sin(p.x * 0.5 + t) * 1.2 + t * 0.4);
          return pow(1.0 - abs((a + b + c) / 3.0), 10.0);
        }
      `)
      .replace('#include <color_fragment>', /* glsl */`#include <color_fragment>
        float caust = caustic(vWaterPos.xz * 0.55, uTime * 0.9) + caustic(vWaterPos.xz * 0.9 + 7.0, uTime * 1.2) * 0.6;
        diffuseColor.rgb += vec3(0.85, 1.0, 0.95) * caust * (0.03 + vShallow * 0.2) * ${causticScale.toFixed(2)};
      `)
      .replace('#include <roughnessmap_fragment>', /* glsl */`#include <roughnessmap_fragment>
        // Calm harbour water is less of a mirror, so it doesn't glare white
        roughnessFactor = mix(roughnessFactor, 0.55, vCalm);
      `)
      .replace('#include <normal_fragment_maps>', /* glsl */`#include <normal_fragment_maps>
        {
          vec2 p = vWaterPos.xz;
          float t = uTime;
          vec2 g = vec2(0.25, 0.0) * cos(p.x * 0.25 + t * 1.2) * 0.05
                 + vec2(0.0, 0.31) * cos(p.y * 0.31 - t * 0.9) * 0.04;
          // Fine ripples from many directions at unrelated wavelengths, so
          // the glitter never settles into a regular lattice
          for (int i = 0; i < 7; i++) {
            float fi = float(i);
            float angle = fi * 2.399 + 0.4;
            vec2 dir = vec2(cos(angle), sin(angle));
            float freq = 0.85 * pow(1.37, fi);
            float amp = 0.05 / (1.0 + fi * 0.6);
            g += dir * cos(dot(p, dir) * freq + t * (1.6 + fi * 0.37) + fi * 1.7) * amp;
          }
          // Sheltered harbours are glassy
          g *= 1.0 - vCalm * 0.55;
          vec3 wn = normalize(vec3(-g.x, 1.0, -g.y));
          normal = normalize((viewMatrix * vec4(wn, 0.0)).xyz);
        }
      `);
  };
  material.customProgramCacheKey = () => `naval-water-${roughness}-${causticScale}`;
  return material;
}

// A piece of scenery on the boundary that fades out of the camera's way,
// exposing the same occluder API as an Obstacle.
class Structure {
  constructor(scene, data, build) {
    this.scene = scene;
    this.data = data;
    this.mesh = new THREE.Group();
    build(this);
    this.mesh.position.set(data.position.x, 0, data.position.z);
    scene.add(this.mesh);
    this.mesh.updateMatrixWorld(true);
    this.fader = new Fader(this.mesh);
  }

  setFadeTarget(opacity) {
    this.fader.target = opacity;
  }

  update(deltaTime) {
    this.fader.update(deltaTime);
  }
}

// The world around and beneath the fighting: sky, ocean, reef, distant islands,
// ships and gulls, and the lagoon inside the harbour walls.
export class Arena {
  constructor(scene) {
    this.scene = scene;
    this.rand = seededRandom(1717);
    this.boundary = [];
    this.followers = [];
    this.ships = [];
    this.gulls = [];
    this.clouds = [];
    this.smoke = [];
    this.create();
  }

  create() {
    this.createSky();
    this.createLagoon();
    this.createOcean();
    this.createReef();
    this.createWalls();
    this.createIslands();
    this.createShips();
    this.createGulls();
    this.createClouds();
  }

  createSky() {
    this.skyUniforms = {
      topColor: { value: new THREE.Color(0x2f7fd0) },
      horizonColor: { value: new THREE.Color(0xd2eef4) },
      bottomColor: { value: new THREE.Color(0xc4e6ee) },
      sunDirection: { value: new THREE.Vector3(0, 1, 0) }
    };
    const sky = new THREE.Mesh(
      new THREE.SphereGeometry(1700, 32, 16),
      new THREE.ShaderMaterial({
        uniforms: this.skyUniforms,
        side: THREE.BackSide,
        depthWrite: false,
        fog: false,
        vertexShader: /* glsl */`
          varying vec3 vDir;
          void main() {
            vDir = normalize(position);
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragmentShader: /* glsl */`
          uniform vec3 topColor;
          uniform vec3 horizonColor;
          uniform vec3 bottomColor;
          uniform vec3 sunDirection;
          varying vec3 vDir;
          void main() {
            vec3 dir = normalize(vDir);
            float h = dir.y;
            vec3 col = mix(horizonColor, topColor, pow(smoothstep(0.0, 0.7, h), 0.6));
            col = mix(col, bottomColor, smoothstep(0.02, -0.1, h));
            float d = max(dot(dir, sunDirection), 0.0);
            col += vec3(1.0, 0.93, 0.78) * (pow(d, 12.0) * 0.25 + pow(d, 200.0) * 0.6);
            col += vec3(1.0, 0.98, 0.9) * smoothstep(0.9995, 0.9998, d) * 1.5;
            gl_FragColor = vec4(col, 1.0);
            #include <tonemapping_fragment>
            #include <colorspace_fragment>
          }
        `
      })
    );
    sky.renderOrder = -2;
    sky.frustumCulled = false;
    this.sky = sky;
    this.scene.add(sky);
    this.followers.push(sky);
  }

  // Turquoise lagoon inside the walls: pale sandbars along the routes and
  // around the base islands, deep channels and darker reef patches elsewhere.
  createLagoon() {
    const size = ARENA_SIZE + 4;
    const geometry = new THREE.PlaneGeometry(size, size, 150, 150);
    geometry.rotateX(-Math.PI / 2);
    const pos = geometry.attributes.position;
    const colors = new Float32Array(pos.count * 3);
    const shallowAttr = new Float32Array(pos.count);
    const calmAttr = new Float32Array(pos.count);
    const harbour = new THREE.Color(0x45c2c6);

    const deep = new THREE.Color(0x1584a6);
    const mid = new THREE.Color(0x24afbf);
    const shallow = new THREE.Color(0x63d3c8);
    const bar = new THREE.Color(0x9fe2cc);
    const reef = new THREE.Color(0x1d6e78);
    const c = new THREE.Color();

    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const z = pos.getZ(i);
      const s = shallowness(x, z);
      const n = fbm(x / 30, z / 30, 3);
      c.copy(deep).lerp(mid, smoothstep(-0.3, 0.4, n) * 0.7);
      c.lerp(shallow, smoothstep(0.1, 0.7, s));
      c.lerp(bar, smoothstep(0.75, 1.0, s) * 0.6);
      // Dark seagrass and coral patches in the deeper water
      const weed = smoothstep(0.3, 0.5, fbm(x / 14 - 9, z / 14 + 3, 3)) * (1 - s);
      c.lerp(reef, weed * 0.6);
      // Calm, pale harbour water inside the bases
      const calm = harbourCalm(x, z);
      c.lerp(harbour, calm * 0.6);
      calmAttr[i] = calm;
      colors[i * 3] = c.r;
      colors[i * 3 + 1] = c.g;
      colors[i * 3 + 2] = c.b;
      shallowAttr[i] = s;
    }
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geometry.setAttribute('aShallow', new THREE.BufferAttribute(shallowAttr, 1));
    geometry.setAttribute('aCalm', new THREE.BufferAttribute(calmAttr, 1));

    const water = new THREE.Mesh(geometry, waterMaterial(0.3, 1));
    water.receiveShadow = true;
    this.lagoon = water;
    this.scene.add(water);
  }

  // Open sea beyond the walls, darkening towards the horizon.
  createOcean() {
    const geometry = new THREE.PlaneGeometry(OCEAN_SIZE, OCEAN_SIZE, 60, 60);
    geometry.rotateX(-Math.PI / 2);
    const pos = geometry.attributes.position;
    const colors = new Float32Array(pos.count * 3);
    const near = new THREE.Color(0x1f9cb4);
    const far = new THREE.Color(0x13537e);
    const c = new THREE.Color();
    for (let i = 0; i < pos.count; i++) {
      const r = Math.max(Math.abs(pos.getX(i)), Math.abs(pos.getZ(i)));
      c.copy(near).lerp(far, smoothstep(HALF + 20, 600, r));
      colors[i * 3] = c.r;
      colors[i * 3 + 1] = c.g;
      colors[i * 3 + 2] = c.b;
    }
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geometry.setAttribute('aShallow', new THREE.BufferAttribute(new Float32Array(pos.count), 1));
    geometry.setAttribute('aCalm', new THREE.BufferAttribute(new Float32Array(pos.count), 1));
    const ocean = new THREE.Mesh(geometry, waterMaterial(0.22, 0.5));
    ocean.position.y = OCEAN_LEVEL;
    ocean.receiveShadow = true;
    this.scene.add(ocean);
  }

  // A rocky reef and a buoy line just outside the harbour walls.
  createReef() {
    const rand = this.rand;
    const rocks = [];
    for (let i = 0; i < 260; i++) {
      const side = Math.floor(rand() * 4);
      const along = (rand() - 0.5) * (ARENA_SIZE + 40);
      const out = HALF + 4 + rand() * 14;
      const x = side === 0 ? along : side === 1 ? along : side === 2 ? out : -out;
      const z = side === 0 ? out : side === 1 ? -out : along;
      const r = 0.6 + rand() * 1.8;
      rocks.push(rockGeometry(rand, r, vary(rand() < 0.3 ? SEA.rockDark : SEA.rock, rand, 0.12), { x, y: OCEAN_LEVEL - r * 0.25, z, squash: 0.55 }));
    }
    this.scene.add(Object.assign(new THREE.Mesh(mergeGeometries(rocks), matte(0xffffff, { vertexColors: true })), { receiveShadow: true }));

    // Surf breaking on the reef and against the outside of the walls
    const reefFoam = foamMesh(foamFrame(ARENA_SIZE + 22, ARENA_SIZE + 22, 6, 0), OCEAN_LEVEL + 0.06);
    this.scene.add(reefFoam);
    this.scene.add(foamMesh(foamFrame(ARENA_SIZE + 3.2, ARENA_SIZE + 3.2, 2.5, 0), OCEAN_LEVEL + 0.05));

    // Marker buoys strung on a rope beyond the reef
    const parts = [];
    const out = HALF + 26;
    const spacing = 14;
    const red = new THREE.Color(SEA.red);
    for (const [ax, az, bx, bz] of [[-out, -out, out, -out], [out, -out, out, out], [out, out, -out, out], [-out, out, -out, -out]]) {
      const length = Math.hypot(bx - ax, bz - az);
      const count = Math.round(length / spacing);
      let prev = null;
      for (let i = 0; i <= count; i++) {
        const t = i / count;
        const p = new THREE.Vector3(ax + (bx - ax) * t, OCEAN_LEVEL, az + (bz - az) * t);
        parts.push(...buoyParts(i % 2 ? red : SEA.white, { x: p.x, y: p.y, z: p.z, height: 1.4 }));
        const top = p.clone().setY(OCEAN_LEVEL + 0.35);
        if (prev) parts.push(...ropeParts(prev, top, { sag: 0.25, radius: 0.06 }));
        prev = top;
      }
    }
    this.scene.add(Object.assign(new THREE.Mesh(mergeGeometries(parts), matte(0xffffff, { vertexColors: true })), { castShadow: true }));
  }

  // Stone harbour walls on the boundary footprint, lighthouses at the corners.
  createWalls() {
    const sides = [
      { id: 'boundary_n', position: { x: 0, y: 0, z: -HALF - WALL_THICKNESS / 2 }, size: { x: ARENA_SIZE + 3, y: WALL_HEIGHT, z: WALL_THICKNESS } },
      { id: 'boundary_s', position: { x: 0, y: 0, z: HALF + WALL_THICKNESS / 2 }, size: { x: ARENA_SIZE + 3, y: WALL_HEIGHT, z: WALL_THICKNESS } },
      { id: 'boundary_e', position: { x: HALF + WALL_THICKNESS / 2, y: 0, z: 0 }, size: { x: WALL_THICKNESS, y: WALL_HEIGHT, z: ARENA_SIZE } },
      { id: 'boundary_w', position: { x: -HALF - WALL_THICKNESS / 2, y: 0, z: 0 }, size: { x: WALL_THICKNESS, y: WALL_HEIGHT, z: ARENA_SIZE } }
    ];
    for (const side of sides) {
      this.boundary.push(new Structure(this.scene, { ...side, type: 'wall', rotation: 0 }, s => this.buildHarbourWall(s, side.size)));
    }
    for (const [x, z] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) {
      const data = { id: `lighthouse_${x}_${z}`, type: 'pillar', position: { x: x * (HALF + 1), y: 0, z: z * (HALF + 1) }, size: { x: 6, y: 14, z: 6 }, rotation: 0 };
      this.boundary.push(new Structure(this.scene, data, s => this.buildLighthouse(s, x * 7 + z)));
    }

    // Foam where the lagoon laps against the inside of the walls
    this.scene.add(foamMesh(foamFrame(ARENA_SIZE + 0.2, ARENA_SIZE + 0.2, -1.8, 0), 0.05));
  }

  // Coursed sea-worn blocks with a weed-and-barnacle tide line, coping and bollards.
  buildHarbourWall(structure, size) {
    const rand = seededRandom(size.x * 13 + size.z * 7 + structure.data.position.x + structure.data.position.z * 3);
    const alongX = size.x >= size.z;
    const length = alongX ? size.x : size.z;
    const thickness = WALL_THICKNESS;
    const ry = alongX ? 0 : Math.PI / 2;
    const parts = [];
    const add = (geo, color, x, y, z) => parts.push(colored(geo, color, { x, y, z }).rotateY(ry));

    const courseHeight = 0.9;
    const top = WALL_HEIGHT - 0.45;
    const courses = Math.round(top / courseHeight);
    const h = top / courses;
    for (let row = 0; row < courses; row++) {
      let cursor = -length / 2;
      let first = row % 2 ? 0.8 + rand() : 1.6 + rand();
      while (cursor < length / 2 - 0.01) {
        const block = Math.min(first, length / 2 - cursor);
        first = 1.6 + rand() * 1.6;
        const color = row === 0 ? vary(SEA.stoneDark, rand, 0.1) : vary(SEA.stone, rand, 0.12, 0.02);
        add(new THREE.BoxGeometry(Math.max(0.05, block - 0.08), h - 0.07, thickness - rand() * 0.1), color, cursor + block / 2, row * h + h / 2, 0);
        cursor += block;
      }
    }
    add(new THREE.BoxGeometry(length - 0.05, top, thickness * 0.8), SEA.stoneDark, 0, top / 2, 0);
    // Coping slabs, overhanging slightly
    add(new THREE.BoxGeometry(length, 0.45, thickness + 0.35), SEA.stone, 0, top + 0.22, 0);

    // Tide line: weed and barnacles on both faces
    for (const side of [-1, 1]) {
      const face = side * (thickness / 2 + 0.04);
      for (let x = -length / 2 + 1; x < length / 2 - 1; x += 1.4 + rand() * 1.2) {
        add(new THREE.BoxGeometry(1 + rand(), 0.35 + rand() * 0.45, 0.08), vary(SEA.seaweed, rand, 0.1), x, 0.25, face);
        if (rand() < 0.6) add(new THREE.BoxGeometry(0.14, 0.14, 0.1), SEA.barnacle, x + rand(), 0.7 + rand() * 0.5, face + side * 0.03);
      }
    }
    // Iron bollards along the top
    for (let x = -length / 2 + 6; x < length / 2 - 4; x += 12) {
      add(new THREE.CylinderGeometry(0.22, 0.28, 0.5, 8), SEA.iron, x, top + 0.7, 0);
      add(new THREE.CylinderGeometry(0.3, 0.3, 0.1, 8), SEA.iron, x, top + 0.98, 0);
    }

    structure.mesh.add(Object.assign(new THREE.Mesh(mergeGeometries(parts), matte(0xffffff, { vertexColors: true })), { castShadow: true, receiveShadow: true }));
  }

  // White, red-banded lighthouse on a rock pile, with a glowing lamp room.
  buildLighthouse(structure, seed) {
    const rand = seededRandom(seed + 100);
    const parts = [];
    for (let i = 0; i < 9; i++) {
      const a = (i / 9) * Math.PI * 2;
      parts.push(rockGeometry(rand, 1.6 + rand(), vary(SEA.rock, rand, 0.12), { x: Math.cos(a) * 2.6, y: 0.3, z: Math.sin(a) * 2.6, squash: 0.6 }));
    }
    parts.push(colored(new THREE.CylinderGeometry(2.6, 3.0, 1.4, 10), SEA.stone, { y: 0.7 }));
    const bands = 5;
    const towerHeight = 10;
    for (let i = 0; i < bands; i++) {
      const t0 = i / bands;
      const t1 = (i + 1) / bands;
      const r0 = 2.0 - t0 * 0.8;
      const r1 = 2.0 - t1 * 0.8;
      parts.push(colored(new THREE.CylinderGeometry(r1, r0, towerHeight / bands, 12), i % 2 ? SEA.red : SEA.white, { y: 1.4 + (t0 + t1) / 2 * towerHeight }));
    }
    const gallery = 1.4 + towerHeight;
    parts.push(colored(new THREE.CylinderGeometry(1.7, 1.5, 0.3, 12), SEA.iron, { y: gallery + 0.15 }));
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2;
      parts.push(colored(new THREE.BoxGeometry(0.06, 0.6, 0.06), SEA.iron, { x: Math.cos(a) * 1.6, y: gallery + 0.6, z: Math.sin(a) * 1.6 }));
    }
    parts.push(colored(new THREE.TorusGeometry(1.6, 0.04, 4, 24), SEA.iron, { rx: Math.PI / 2, y: gallery + 0.9 }));
    parts.push(colored(new THREE.ConeGeometry(1.25, 1.2, 12), SEA.red, { y: gallery + 2.3 }));
    parts.push(colored(new THREE.SphereGeometry(0.2, 6, 4), SEA.iron, { y: gallery + 3.0 }));
    structure.mesh.add(Object.assign(new THREE.Mesh(mergeGeometries(parts), matte(0xffffff, { vertexColors: true })), { castShadow: true, receiveShadow: true }));

    // The lamp room is light: it doesn't fade
    const lamp = new THREE.Mesh(new THREE.CylinderGeometry(0.85, 0.85, 1.4, 12), new THREE.MeshBasicMaterial({ color: SEA.lamp, toneMapped: false }));
    lamp.position.y = gallery + 1.0;
    lamp.userData.noFade = true;
    structure.mesh.add(lamp);
  }

  // Volcanic islands, palm-fringed atolls and a smoking peak on the horizon.
  createIslands() {
    const rand = seededRandom(808);
    const land = [];
    const palms = [];
    const islands = [
      { angle: -1.9, dist: 520, r: 70, peak: 150, volcano: true },
      { angle: -1.2, dist: 700, r: 90, peak: 90 },
      { angle: -0.4, dist: 460, r: 45, peak: 40 },
      { angle: 0.5, dist: 620, r: 60, peak: 70 },
      { angle: 1.3, dist: 420, r: 35, peak: 18 },
      { angle: 2.0, dist: 760, r: 110, peak: 120, volcano: true },
      { angle: 2.7, dist: 480, r: 40, peak: 30 },
      { angle: 3.5, dist: 560, r: 55, peak: 55 },
      { angle: 4.3, dist: 400, r: 30, peak: 14 },
      { angle: 4.9, dist: 650, r: 75, peak: 85 }
    ];
    for (const island of islands) {
      const cx = Math.cos(island.angle) * island.dist;
      const cz = Math.sin(island.angle) * island.dist;
      const r = island.r;
      // Beach ring, then green slopes, then rock (or a dark volcanic cone)
      land.push(colored(jitter(new THREE.CylinderGeometry(r, r * 1.15, 3, 18, 1), r * 0.06, rand), SEA.sand, { x: cx, y: OCEAN_LEVEL + 0.5, z: cz }));
      const hill = jitter(new THREE.ConeGeometry(r * 0.85, island.peak, 14, 4), r * 0.08, rand);
      // Colour each face by height: forest low, rock high
      const flat = hill.toNonIndexed();
      flat.computeVertexNormals();
      const p = flat.attributes.position;
      const colors = new Float32Array(p.count * 3);
      const col = new THREE.Color();
      for (let i = 0; i < p.count; i += 3) {
        const y = (p.getY(i) + p.getY(i + 1) + p.getY(i + 2)) / 3 + island.peak / 2;
        const t = y / island.peak;
        if (island.volcano) {
          col.set(t > 0.55 ? 0x4a4440 : t > 0.3 ? 0x5f6a3a : SEA.grassDark);
        } else {
          col.set(t > 0.75 ? SEA.rock : t > 0.15 ? SEA.grassDark : SEA.grass);
        }
        col.offsetHSL(0, 0, (rand() - 0.5) * 0.05);
        for (let k = 0; k < 3; k++) colors.set([col.r, col.g, col.b], (i + k) * 3);
      }
      flat.setAttribute('color', new THREE.BufferAttribute(colors, 3));
      flat.deleteAttribute('uv');
      flat.translate(cx, island.peak / 2 + 1, cz);
      land.push(flat);

      // Palms around the shore
      const count = Math.round(r / 3);
      for (let i = 0; i < count; i++) {
        const a = rand() * Math.PI * 2;
        const d = r * (0.8 + rand() * 0.25);
        palms.push([cx + Math.cos(a) * d, 1.2, cz + Math.sin(a) * d, rand() * Math.PI * 2, 1.4 + rand() * 1.4]);
      }
      if (island.volcano) this.smokeSources = [...(this.smokeSources || []), new THREE.Vector3(cx, island.peak + 2, cz)];
    }
    const islandMesh = new THREE.Mesh(mergeGeometries(land), matte(0xffffff, { vertexColors: true }));
    this.scene.add(islandMesh);

    const palm = mergeGeometries(palmParts(seededRandom(5)));
    const palmMesh = new THREE.InstancedMesh(palm, matte(0xffffff, { vertexColors: true }), palms.length);
    const dummy = new THREE.Object3D();
    palms.forEach(([x, y, z, ry, s], i) => {
      dummy.position.set(x, y, z);
      dummy.rotation.set(0, ry, 0);
      dummy.scale.setScalar(s);
      dummy.updateMatrix();
      palmMesh.setMatrixAt(i, dummy.matrix);
    });
    this.scene.add(palmMesh);

    // A lazy smoke plume rising from each volcano
    const smokeMaterial = new THREE.MeshLambertMaterial({ color: 0xd8d4cc, transparent: true, opacity: 0.55, flatShading: true, depthWrite: false });
    const puff = new THREE.IcosahedronGeometry(8, 0);
    for (const source of this.smokeSources || []) {
      for (let i = 0; i < 9; i++) {
        const mesh = new THREE.Mesh(puff, smokeMaterial);
        mesh.userData = { source, phase: i / 9, drift: rand() * 10 };
        this.scene.add(mesh);
        this.smoke.push(mesh);
      }
    }
  }

  // Tall ships tacking slowly across the horizon.
  createShips() {
    const rand = seededRandom(1492);
    const hull = mergeGeometries([
      colored(new THREE.BoxGeometry(5, 2.2, 18), SEA.woodDark, { y: 0.6 }),
      colored(new THREE.ConeGeometry(2.6, 6, 4, 1), SEA.woodDark, { rx: Math.PI / 2, ry: Math.PI / 4, y: 0.6, z: 11.5, scale: [1, 1, 0.6] }),
      colored(new THREE.BoxGeometry(5.2, 0.4, 18.2), SEA.wood, { y: 1.8 }),
      colored(new THREE.BoxGeometry(5.1, 0.35, 18.1), 0xe8d9a8, { y: 0.2 }),
      colored(new THREE.BoxGeometry(4.6, 2, 4), SEA.wood, { y: 2.8, z: -7 }),
      ...[-4, 2, 7].flatMap((z, i) => {
        const h = i === 1 ? 20 : 16;
        return [
          colored(new THREE.CylinderGeometry(0.2, 0.3, h, 6), SEA.woodDark, { y: 1.8 + h / 2, z }),
          colored(new THREE.BoxGeometry(6.5, 0.18, 0.18), SEA.woodDark, { y: 1.8 + h * 0.5, z }),
          colored(new THREE.BoxGeometry(5, 0.15, 0.15), SEA.woodDark, { y: 1.8 + h * 0.85, z })
        ];
      })
    ]);
    // Billowing sails: bent planes
    const sail = (w, h, y, z) => {
      const geo = new THREE.PlaneGeometry(w, h, 4, 2);
      const p = geo.attributes.position;
      for (let i = 0; i < p.count; i++) {
        const u = p.getX(i) / (w / 2);
        const v = p.getY(i) / (h / 2);
        p.setZ(i, (1 - u * u) * 0.9 * (1 - v * v * 0.3));
      }
      geo.computeVertexNormals();
      return colored(geo, SEA.canvas, { y, z: z + 0.3 });
    };
    const sails = mergeGeometries([-4, 2, 7].flatMap((z, i) => {
      const h = i === 1 ? 20 : 16;
      return [sail(6.2, h * 0.32, 1.8 + h * 0.66, z), sail(5.2, h * 0.24, 1.8 + h * 0.92 - h * 0.15, z)];
    }));
    const hullMaterial = matte(0xffffff, { vertexColors: true });
    const sailMaterial = matte(0xffffff, { vertexColors: true, unique: true });
    sailMaterial.side = THREE.DoubleSide;

    for (let i = 0; i < 6; i++) {
      const ship = new THREE.Group();
      ship.add(new THREE.Mesh(hull, hullMaterial));
      const sailMesh = new THREE.Mesh(sails, sailMaterial);
      ship.add(sailMesh);
      const flag = new THREE.Mesh(new THREE.PlaneGeometry(2.2, 1.2), matte(i % 2 ? SEA.red : 0x2a5fb0, { unique: true }));
      flag.material.side = THREE.DoubleSide;
      flag.position.set(0, 1.8 + 20 + 0.9, 2 - 1.1);
      flag.rotation.y = Math.PI / 2;
      ship.add(flag);
      ship.scale.setScalar(1.3 + rand() * 0.8);
      ship.userData = {
        radius: 300 + rand() * 360,
        angle: rand() * Math.PI * 2,
        speed: (rand() < 0.5 ? -1 : 1) * (3 + rand() * 3),
        phase: rand() * 10
      };
      this.scene.add(ship);
      this.ships.push(ship);
    }
  }

  // Gulls wheeling over the lagoon and the reef.
  createGulls() {
    const rand = seededRandom(3);
    const bodyGeo = colored(new THREE.ConeGeometry(0.18, 0.9, 5), SEA.white, { rx: Math.PI / 2 });
    const wingGeo = new THREE.BufferGeometry();
    wingGeo.setAttribute('position', new THREE.Float32BufferAttribute([0, 0, 0.25, 1.3, 0.1, -0.05, 0, 0, -0.25, 1.3, 0.1, -0.05, 1.3, 0.1, -0.05, 0.6, 0.05, -0.3], 3));
    wingGeo.computeVertexNormals();
    const tipGeo = new THREE.BufferGeometry();
    tipGeo.setAttribute('position', new THREE.Float32BufferAttribute([1.3, 0.1, -0.05, 1.8, 0.0, -0.25, 1.1, 0.08, -0.25], 3));
    tipGeo.computeVertexNormals();
    const white = matte(SEA.white, { unique: true });
    white.side = THREE.DoubleSide;
    const tipMaterial = matte(0x4a4a4e, { unique: true });
    tipMaterial.side = THREE.DoubleSide;

    for (let i = 0; i < 26; i++) {
      const gull = new THREE.Group();
      gull.add(new THREE.Mesh(bodyGeo, white));
      for (const side of [-1, 1]) {
        const wing = new THREE.Group();
        wing.add(new THREE.Mesh(wingGeo, white), new THREE.Mesh(tipGeo, tipMaterial));
        wing.scale.x = side;
        gull.add(wing);
      }
      gull.scale.setScalar(1.4 + rand() * 0.6);
      gull.userData = {
        cx: (rand() - 0.5) * 260,
        cz: (rand() - 0.5) * 260,
        radius: 15 + rand() * 40,
        height: 22 + rand() * 25,
        speed: (rand() < 0.5 ? -1 : 1) * (0.25 + rand() * 0.2),
        phase: rand() * Math.PI * 2
      };
      this.scene.add(gull);
      this.gulls.push(gull);
    }
  }

  createClouds() {
    const rand = seededRandom(77);
    const material = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 1, flatShading: true, transparent: true, opacity: 0.94 });
    for (let i = 0; i < 22; i++) {
      const puffs = [];
      const n = 3 + Math.floor(rand() * 4);
      for (let p = 0; p < n; p++) {
        puffs.push(colored(jitter(new THREE.IcosahedronGeometry(9 + rand() * 9, 0), 3, rand), 0xffffff, {
          x: (p - n / 2) * 11 + rand() * 4, y: rand() * 4, z: (rand() - 0.5) * 10
        }));
      }
      const cloud = new THREE.Mesh(mergeGeometries(puffs), material);
      cloud.scale.y = 0.5;
      cloud.position.set((rand() - 0.5) * 1400, 170 + rand() * 80, (rand() - 0.5) * 1400);
      cloud.userData.speed = 2 + rand() * 3;
      this.scene.add(cloud);
      this.clouds.push(cloud);
    }
  }

  // Called each frame with the sun direction from the Scene.
  update(deltaTime, elapsed, ambient, focus) {
    if (ambient) this.skyUniforms.sunDirection.value.copy(ambient.sunDirection);
    if (focus) {
      for (const object of this.followers) object.position.set(focus.x, 0, focus.z);
    }

    for (const cloud of this.clouds) {
      cloud.position.x += cloud.userData.speed * deltaTime;
      if (cloud.position.x > 700) cloud.position.x = -700;
    }

    for (const ship of this.ships) {
      const u = ship.userData;
      const a = u.angle + (elapsed * u.speed) / u.radius;
      ship.position.set(Math.cos(a) * u.radius, OCEAN_LEVEL + Math.sin(elapsed * 0.8 + u.phase) * 0.3, Math.sin(a) * u.radius);
      // Sail along the direction of travel, rolling gently in the swell
      ship.rotation.set(Math.sin(elapsed * 0.6 + u.phase) * 0.03, -a + (u.speed > 0 ? 0 : Math.PI), Math.sin(elapsed * 0.7 + u.phase) * 0.05);
    }

    for (const gull of this.gulls) {
      const u = gull.userData;
      const a = u.phase + elapsed * u.speed;
      gull.position.set(u.cx + Math.cos(a) * u.radius, u.height + Math.sin(elapsed * 0.7 + u.phase) * 2, u.cz + Math.sin(a) * u.radius);
      gull.rotation.set(0, -a + (u.speed > 0 ? 0 : Math.PI), -Math.sign(u.speed) * 0.25);
      // Flap in bursts, then glide
      const flap = Math.sin(elapsed * 9 + u.phase * 5) * 0.5 * Math.max(0, Math.sin(elapsed * 0.6 + u.phase));
      gull.children[1].rotation.z = flap;
      gull.children[2].rotation.z = -flap;
    }

    for (const puff of this.smoke) {
      const u = puff.userData;
      const t = (elapsed * 0.025 + u.phase) % 1;
      puff.position.set(u.source.x + t * 70 + Math.sin(t * 6 + u.drift) * 6, u.source.y + t * 90, u.source.z + Math.cos(t * 5 + u.drift) * 6);
      puff.scale.setScalar(0.6 + t * 2.4);
      puff.rotation.y = t * 3 + u.drift;
    }

    for (const piece of this.boundary) piece.update(deltaTime, elapsed);
  }

  getOccluders() {
    return this.boundary;
  }
}
